import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { useApi } from "shared/services/api/useApi";
import { useParams } from "react-router";

export const useProjectByCampaignsIdApi = () => {
    const { getDataAsync } = useApi();
    const { clientId } = useParams();

    const getAllProjectsByCampaignsApiCall = useCallback(
        async (campaignId) => {
            const response = await getDataAsync("projectsFilterByCampaignsId", {
                urlParams: [
                    { name: "$clientId", value: clientId },
                    { name: "$campaignId", value: campaignId },
                ],
            });

            const result = response.status === 204
                ? null
                : response.json();

            return result;
        },
        [getDataAsync, clientId]
    );

    return useMemo(() => {
        const useProjectByCampaignsId = (campaignId) =>
            useQuery(["projectsByCampaignsIdList", campaignId], () =>
                getAllProjectsByCampaignsApiCall(campaignId)
            );
        return {
            useProjectByCampaignsId,
        };
    }, [getAllProjectsByCampaignsApiCall]);
};

const useClientCampaigns = () => {
    const { getDataAsync, putData, postData, deleteData } = useApi();
    const queryClient = useQueryClient();
    const { clientId } = useParams();

    const getClientCampaignsByClientIdApiCall = useCallback(async () => {
        const response = await getDataAsync("clientCampaignsByClientId", {
            urlParams: [{ name: "$clientId", value: clientId }],
        });

        const result = response.status === 204
            ? null
            : response.json();

        return result;

    }, [getDataAsync, clientId]);

    const clientCampaignsByClientId = useQuery(
        "clientCampaignsByIdList",
        getClientCampaignsByClientIdApiCall
    );

    const createClientCampaignApiCall = useCallback(
        ({ clientCampaignId, body }) => {
            return postData("clientCampaigns", {
                urlParams: [{ name: "$campaignId", value: clientCampaignId }],
                body,
            });
        },
        [postData]
    );

    const updateClientCampaignApiCall = useCallback(
        ({ clientCampaignId, body }) => {
            return putData("clientCampaigns", {
                urlParams: [{ name: "$campaignId", value: clientCampaignId }],
                body,
            });
        },
        [putData]
    );

    const deleteClientCampaignApiCall = useCallback(
        ({ clientCampaignId }) => {
            return deleteData("deleteClientCampaign", {
                urlParams: [{ name: "$id", value: clientCampaignId }],
            });
        },
        [deleteData]
    );

    const mutationOptions = useMemo(() => {
        return {
            onSuccess: () => {
                queryClient.invalidateQueries("clientCampaignsByIdList");
            },
        };
    }, [queryClient]);

    const createClientCampaign = useMutation(
        createClientCampaignApiCall,
        mutationOptions
    );
    const updateClientCampaign = useMutation(
        updateClientCampaignApiCall,
        mutationOptions
    );
    const deleteClientCampaign = useMutation(
        deleteClientCampaignApiCall,
        mutationOptions
    );

    return useMemo(() => {
        return {
            clientCampaignsByClientId,
            createClientCampaign,
            updateClientCampaign,
            deleteClientCampaign,
        };
    }, [
        clientCampaignsByClientId,
        createClientCampaign,
        updateClientCampaign,
        deleteClientCampaign,
    ]);
};

export default useClientCampaigns;
